import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getHardwareCategory, getProducts } from '@/lib/api';
import { motion } from 'framer-motion';
import { fadeUp, stagger } from '@/lib/motion';
import { PageHero, Section, SectionTitle } from '@/components/shared/Section';
import PilotBanner from '@/components/shared/PilotBanner';
import ProductGridCard from '@/components/shared/ProductGridCard';
import { ShieldCheck, ArrowRight, MessageCircle } from 'lucide-react';
import { useCart } from '@/components/shared/Cart';

export default function HardwareCategoryPage() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandFilter, setBrandFilter] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const { addItem } = useCart() || { addItem: () => {} };

  useEffect(() => {
    setLoading(true);
    setBrandFilter('');
    setSubFilter('');
    Promise.all([
      getHardwareCategory(slug).catch(() => null),
      getProducts(slug).catch(() => ({ data: [] }))
    ]).then(([cat, prods]) => {
      setCategory(cat?.data || null);
      setProducts(prods?.data || []);
      setLoading(false);
    });
  }, [slug]);

  // Add a robots="noindex" meta tag whenever this category has no products,
  // so Google doesn't waste crawl budget on empty pages.
  useEffect(() => {
    if (loading) return;
    const META_ID = 'meta-robots-dynamic';
    let tag = document.querySelector(`meta[name="robots"][data-id="${META_ID}"]`);
    if (products.length === 0) {
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', 'robots');
        tag.setAttribute('data-id', META_ID);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', 'noindex, follow');
    } else if (tag) {
      tag.remove();
    }
    return () => {
      const t = document.querySelector(`meta[name="robots"][data-id="${META_ID}"]`);
      if (t) t.remove();
    };
  }, [loading, products.length]);

  // Distinct brands and subcategories for filter chips
  const brands = useMemo(() => [...new Set(products.map(p => p.brand).filter(Boolean))], [products]);
  const subcategories = useMemo(() => [...new Set(products.map(p => p.subcategory).filter(Boolean))], [products]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesBrand = !brandFilter || p.brand === brandFilter;
      const matchesSub = !subFilter || p.subcategory === subFilter;
      return matchesBrand && matchesSub;
    });
  }, [products, brandFilter, subFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0EA5E9] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <PageHero
        badge={category ? `HARDWARE / ${category.name.toUpperCase()}` : 'HARDWARE'}
        badgeIcon={ShieldCheck}
        h1={category?.name || 'Categoría Hardware'}
        subtitle={category?.description || 'Productos premium para tu operación.'}
        primaryCTA={
          <Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg">
            Cotizar <ArrowRight className="w-4 h-4" />
          </Link>
        }
        secondaryCTA={
          <a href="https://wa.me/56975294340?text=Hola,%20quiero%20cotizar%20hardware" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-emerald-400 border border-emerald-400/30 rounded-xl hover:bg-emerald-400/10">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
        }
      />

      {products.length > 0 ? (
        <Section>
          <SectionTitle badge="PRODUCTOS" title={`${filteredProducts.length} producto${filteredProducts.length !== 1 ? 's' : ''} en ${category?.name || 'esta categoría'}`} />

          {/* Subcategory filter chips */}
          {subcategories.length > 1 && (
            <div className="flex flex-wrap gap-2 justify-center mb-3" data-testid="filter-subcategory">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold self-center mr-1">Tipo:</span>
              <button
                onClick={() => setSubFilter('')}
                className={`px-3.5 py-2 text-xs font-medium rounded-xl border transition-all ${!subFilter ? 'bg-[#0A2540] text-white border-[#0A2540]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                data-testid="filter-sub-all"
              >
                Todos
              </button>
              {subcategories.map(s => (
                <button
                  key={s}
                  onClick={() => setSubFilter(s)}
                  className={`px-3.5 py-2 text-xs font-semibold rounded-xl border transition-all ${subFilter === s ? 'bg-[#0A2540] text-white border-[#0A2540]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                  data-testid={`filter-sub-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Brand filter chips */}
          {brands.length > 1 && (
            <div className="flex flex-wrap gap-2 justify-center mb-8" data-testid="filter-brand">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold self-center mr-1">Marca:</span>
              <button
                onClick={() => setBrandFilter('')}
                className={`px-3.5 py-2 text-xs font-medium rounded-xl border transition-all ${!brandFilter ? 'bg-[#0A2540] text-white border-[#0A2540]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                data-testid="filter-brand-all"
              >
                Todas
              </button>
              {brands.map(b => (
                <button
                  key={b}
                  onClick={() => setBrandFilter(b)}
                  className={`px-3.5 py-2 text-xs font-semibold rounded-xl border transition-all ${brandFilter === b ? 'bg-[#0A2540] text-white border-[#0A2540]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                  data-testid={`filter-brand-${b}`}
                >
                  {b}
                </button>
              ))}
            </div>
          )}

          <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch" data-testid="hardware-products-grid">
            {filteredProducts.map((p) => (
              <motion.div key={p.slug || p.id} variants={fadeUp}>
                <ProductGridCard product={p} addItem={addItem} />
              </motion.div>
            ))}
          </motion.div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12 text-sm text-slate-500" data-testid="hardware-no-filter-results">
              No hay productos con los filtros seleccionados.
              <button onClick={() => { setBrandFilter(''); setSubFilter(''); }} className="ml-2 text-[#0EA5E9] underline">Limpiar filtros</button>
            </div>
          )}
        </Section>
      ) : (
        <Section>
          <div className="text-center py-12">
            <p className="text-[#64748B] mb-4">Productos disponibles próximamente. Contáctanos para más información.</p>
            <Link to="/contacto" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600">
              Cotizar <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Section>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <PilotBanner />
      </div>

      <Section alt>
        <div className="text-center">
          <Link to="/hardware" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0EA5E9] hover:underline">
            ← Volver al catálogo completo
          </Link>
        </div>
      </Section>
    </div>
  );
}
